import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {selectGlobalSettings, updateGlobalSettings} from '../../features/globalSettings/globalSettingsSlice';
import {fetchMaterials, selectMaterialsStatus, selectVisibleMaterials} from '../../features/materials/materialsSlice';
import {
    fetchEdgebanding,
    selectEdgebandingStatus,
    selectVisibleEdgebanding
} from '../../features/edgebanding/edgebandingSlice';
import './GlobalSettingsSidebar.css';
import {FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaInfoCircle, FaSave} from 'react-icons/fa';
import {useFetchConfigOptions} from "../OrderComponent/hooks/useFetchConfigOptions";
import {resetGlobalSettingsApplied} from "../../features/cart/cartSlice";

function GlobalSettingsSidebar() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Redux selectors
    const globalSettings = useSelector(selectGlobalSettings);
    const materials = useSelector(selectVisibleMaterials);
    const edgebandingOptions = useSelector(selectVisibleEdgebanding);
    const materialsStatus = useSelector(selectMaterialsStatus);
    const edgebandingStatus = useSelector(selectEdgebandingStatus);

    // Local states for configuration options
    const [jointOptions, setJointOptions] = useState([]);
    const [hingeOptions, setHingeOptions] = useState([]);
    const [backPanelOptions, setBackPanelOptions] = useState([]);
    const [drawerOptions, setDrawerOptions] = useState([]);
    const [configOptions, setConfigOptions] = useState([]);

    // Collapse state and mobile detection
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const baseURL = process.env.REACT_APP_BASEURL;

    // Use the config options hook
    useFetchConfigOptions(
        baseURL,
        setConfigOptions,
        setJointOptions,
        setDrawerOptions,
        setHingeOptions,
        setBackPanelOptions
    );

    // Handle resize to detect mobile
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch data if idle (improves performance)
    useEffect(() => {
        if (materialsStatus === 'idle') {
            dispatch(fetchMaterials());
        }
        if (edgebandingStatus === 'idle') {
            dispatch(fetchEdgebanding());
        }
    }, [dispatch, materialsStatus, edgebandingStatus]);

    // Update global settings in Redux
    const handleInputChange = (field, value) => {
        dispatch(updateGlobalSettings({[field]: value}));
    };

    // Original tooltip: navigate to explanation page
    const handleToolTipClick = (field) => {
        navigate("/learning/tool-tip/explain", {state: {field}});
    };

    // Render a dropdown (label + info icon + select)
    const renderDropdown = (label, fieldName, options = []) => (
        <div className="gs-sidebar__form-group">
            <div className="gs-sidebar__label-container">
                <label htmlFor={fieldName} className="gs-sidebar__label">
                    {label}
                </label>
                <FaInfoCircle
                    className="gs-sidebar__info-icon"
                    onClick={() => handleToolTipClick(fieldName)}
                    title="Click for more info"
                />
            </div>
            <select
                id={fieldName}
                className="gs-sidebar__select"
                value={globalSettings[fieldName] || options[0] || ''}
                onChange={(e) => handleInputChange(fieldName, e.target.value)}
            >
                {options.map((option, index) => (
                    <option key={index} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        </div>
    );

    // Toggle collapse
    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Save settings: no navigation, just an alert
    const handleSaveSettings = () => {
        dispatch(resetGlobalSettingsApplied());
        alert("Global Settings Applied Successfully!")


    };

    // Loading states
    if (materialsStatus === 'loading' || edgebandingStatus === 'loading') {
        return <div className="gs-sidebar__loading">Loading...</div>;
    }

    // Decide the main sidebar classes
    const sidebarClass = isMobile ? "gs-sidebar-mobile" : "gs-sidebar-desktop";
    const collapsedClass = isMobile
        ? (isCollapsed ? "gs-sidebar--collapsed-mobile" : "")
        : (isCollapsed ? "gs-sidebar--collapsed" : "");

    // Decide which toggler icon to show
    let TogglerIcon;
    if (isMobile) {
        TogglerIcon = isCollapsed ? <FaChevronDown size={20}/> : <FaChevronUp size={20}/>;
    } else {
        TogglerIcon = isCollapsed ? <FaChevronRight size={20}/> : <FaChevronLeft size={20}/>;
    }

    return (
        <div className={`gs-sidebar ${sidebarClass} ${collapsedClass}`}>
            <div
                className={`gs-sidebar__toggler ${isMobile ? "gs-sidebar__toggler-mobile" : "gs-sidebar__toggler-desktop"}`}
                onClick={toggleSidebar}
                title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
                {TogglerIcon}
            </div>
            {!isCollapsed && (
                <div className="gs-sidebar__content">
                    <h2 className="gs-sidebar__title">Global Settings</h2>
                    {/* Materials Section */}
                    <section className="gs-sidebar__section">
                        <h3 className="gs-sidebar__section-title">Materials</h3>
                        {renderDropdown('Case Material', 'caseMaterial', materials.map(m => m.description))}
                        {renderDropdown('Front Material', 'frontMaterial', materials.map(m => m.description))}
                        {renderDropdown('Back Panel Material', 'backPanelMaterial', materials.map(m => m.description))}
                    </section>
                    {/* Joint Methods Section */}
                    <section className="gs-sidebar__section">
                        <h3 className="gs-sidebar__section-title">Joint Methods</h3>
                        {renderDropdown('Joint Method', 'jointMethod', jointOptions)}
                        {renderDropdown('Back Panel Method', 'backPanel', backPanelOptions)}
                    </section>
                    {/* Edgebanding Section */}
                    <section className="gs-sidebar__section">
                        <h3 className="gs-sidebar__section-title">Edgebanding</h3>
                        {renderDropdown('Case Edge', 'caseEdge', edgebandingOptions.map(o => o.englishDescription))}
                        {renderDropdown('Front Edge', 'frontEdge', edgebandingOptions.map(o => o.englishDescription))}
                    </section>
                    {/* Door Settings Section */}
                    <section className="gs-sidebar__section">
                        <h3 className="gs-sidebar__section-title">Door Settings</h3>
                        {renderDropdown('Hinge Options', 'hingePlate', hingeOptions)}
                    </section>
                    {/* Drawer Settings Section */}
                    <section className="gs-sidebar__section">
                        <h3 className="gs-sidebar__section-title">Drawer Settings</h3>
                        {renderDropdown('Drawer Options', 'drawerType', drawerOptions)}
                    </section>
                    {/* Save Button */}
                    <button className="gs-sidebar__save-button" onClick={handleSaveSettings}>
                        <FaSave/> Save Settings
                    </button>
                </div>
            )}
        </div>
    );
}

export default GlobalSettingsSidebar;
